import { ALL_POLICIES } from "../../app/pages/TaxPolicies";
import type { TaxPolicy, TaxPolicyStore } from "./types";

// Transform ALL_POLICIES (which doesn't have ids in its in-page definition)
// into TaxPolicy records with deterministic ids so Expense.taxPolicyId
// references survive a localStorage round-trip.
function deriveId(category: string, region: string, idx: number): string {
  const slug = category
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  const regionSlug = region.toLowerCase().replace(/[^a-z0-9]+/g, "");
  return `tp_${regionSlug}_${slug}_${idx}`;
}

// Best-effort mapping from a tax policy's free-text `category` column to the
// id of a seeded localStorage category (see `DEFAULT_CATEGORIES` in
// categories/localStorageStore). Mirrors the sub-select in
// `supabase/migrations/0004_tax_policy_category_fk.sql`:
// some policy rows (e.g. "Client Gifts", "Home Office") intentionally have
// no matching category — they fall through to `null`.
const POLICY_CATEGORY_TO_CAT_ID: Record<string, string> = {
  "Business Meals": "cat_meals",
  Entertainment: "cat_entertainment",
  "Entertainment & Client Events": "cat_entertainment",
  "Business Travel (Airfare & Lodging)": "cat_travel",
  "Business Travel (Flights & Hotels)": "cat_travel",
  "Vehicle / Mileage": "cat_travel",
  "Company Vehicles": "cat_travel",
  "Software & SaaS Subscriptions": "cat_software",
  "Software & Digital Services (B2B Cross-border)": "cat_software",
  "Office Supplies & Equipment": "cat_office",
  "Advertising & Marketing": "cat_marketing",
  "Professional Development & Education": "cat_payroll_related",
};

const SEED: TaxPolicy[] = ALL_POLICIES.map((p, i) => ({
  id: deriveId(p.category, p.region, i),
  category: p.category,
  categoryId: POLICY_CATEGORY_TO_CAT_ID[p.category] ?? null,
  region: p.region,
  deductibility: p.deductibility,
  vatRule: p.vatRule,
  requirements: p.requirements,
  note: p.note ?? null,
  reference: p.reference,
  referenceUrl: p.referenceUrl,
  status: p.status,
}));

export function createLocalStorageTaxPolicyStore(): TaxPolicyStore {
  const listeners = new Set<() => void>();

  // Static snapshot — tax policies are read-only for now.
  const snapshot: TaxPolicy[] = SEED;

  return {
    list(): TaxPolicy[] {
      return snapshot;
    },
    subscribe(listener: () => void): () => void {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    async refresh(): Promise<void> {
      // Static seed; API parity only.
      return;
    },
  };
}
